import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useTask } from '../contexts/TaskContext';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { subscribeToGoals, createGoal, updateGoal, deleteGoal } from '../services/firestore';
import toast from 'react-hot-toast';
import { Target, Plus, X, Trash2, ChevronDown, ChevronRight, Link2, TrendingUp, CheckCircle2, Circle, Edit3 } from 'lucide-react';

const STATUS_CONFIG = {
  'en-curso': { label: 'En curso', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  'en-riesgo': { label: 'En riesgo', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  'completada': { label: 'Completada', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  'pausada': { label: 'Pausada', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
};

const GoalsPage = () => {
  const { user } = useAuth();
  const { projects = [], tasks = [] } = useTask();
  const [goals, setGoals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [expandedGoals, setExpandedGoals] = useState({});

  // Form state
  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    estado: 'en-curso',
    fechaLimite: '',
    linkedProjects: [],
    keyResults: [],
  });
  const [newKR, setNewKR] = useState('');

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeToGoals(user.uid, setGoals);
    return () => unsub?.();
  }, [user?.uid]);

  const resetForm = () => {
    setForm({ titulo: '', descripcion: '', estado: 'en-curso', fechaLimite: '', linkedProjects: [], keyResults: [] });
    setNewKR('');
    setEditingGoal(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.titulo.trim()) return toast.error('El titulo es requerido');
    try {
      if (editingGoal) {
        await updateGoal(user.uid, editingGoal.id, form);
        toast.success('Meta actualizada');
      } else {
        await createGoal(user.uid, form);
        toast.success('Meta creada');
      }
      resetForm();
    } catch {
      toast.error('Error al guardar meta');
    }
  };

  const handleEdit = (goal) => {
    setForm({
      titulo: goal.titulo || '',
      descripcion: goal.descripcion || '',
      estado: goal.estado || 'en-curso',
      fechaLimite: goal.fechaLimite || '',
      linkedProjects: goal.linkedProjects || [],
      keyResults: goal.keyResults || [],
    });
    setEditingGoal(goal);
    setShowForm(true);
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Eliminar esta meta?')) return;
    try {
      await deleteGoal(user.uid, goalId);
      toast.success('Meta eliminada');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleKR = async (goalId, krIndex) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const krs = [...(goal.keyResults || [])];
    krs[krIndex] = { ...krs[krIndex], done: !krs[krIndex].done };
    try {
      await updateGoal(user.uid, goalId, { keyResults: krs });
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const addKeyResult = () => {
    if (!newKR.trim()) return;
    setForm(prev => ({
      ...prev,
      keyResults: [...prev.keyResults, { text: newKR.trim(), done: false }],
    }));
    setNewKR('');
  };

  const removeKeyResult = (index) => {
    setForm(prev => ({
      ...prev,
      keyResults: prev.keyResults.filter((_, i) => i !== index),
    }));
  };

  const toggleProject = (projectId) => {
    setForm(prev => ({
      ...prev,
      linkedProjects: prev.linkedProjects.includes(projectId)
        ? prev.linkedProjects.filter(id => id !== projectId)
        : [...prev.linkedProjects, projectId],
    }));
  };

  // Calculate progress for a goal based on key results
  const getProgress = (goal) => {
    const krs = goal.keyResults || [];
    if (krs.length === 0) return goal.estado === 'completada' ? 100 : 0;
    const done = krs.filter(kr => kr.done).length;
    return Math.round((done / krs.length) * 100);
  };

  // Count linked project tasks
  const getLinkedTaskStats = (goal) => {
    const linkedIds = goal.linkedProjects || [];
    if (linkedIds.length === 0) return { total: 0, done: 0 };
    const linkedTasks = tasks.filter(t => linkedIds.includes(t.projectId));
    return {
      total: linkedTasks.length,
      done: linkedTasks.filter(t => t.estado === 'finalizado').length,
    };
  };

  const stats = useMemo(() => {
    const total = goals.length;
    const completed = goals.filter(g => g.estado === 'completada').length;
    const atRisk = goals.filter(g => g.estado === 'en-riesgo').length;
    const avgProgress = total > 0 ? Math.round(goals.reduce((acc, g) => acc + getProgress(g), 0) / total) : 0;
    return { total, completed, atRisk, avgProgress };
  }, [goals]);

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Metas y Portfolios</h1>
              <p className="text-xs text-gray-500">Define objetivos y vincula proyectos para medir progreso</p>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition"
          >
            <Plus className="w-4 h-4" /> Nueva Meta
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-800 dark:text-gray-100' },
            { label: 'Completadas', value: stats.completed, color: 'text-green-600' },
            { label: 'En riesgo', value: stats.atRisk, color: 'text-amber-600' },
            { label: 'Progreso prom.', value: `${stats.avgProgress}%`, color: 'text-purple-600' },
          ].map((s, i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-gray-500 uppercase font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Goals List */}
      <div className="flex-1 overflow-y-auto p-6">
        {goals.length === 0 && !showForm ? (
          <div className="text-center py-16">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No hay metas definidas</p>
            <p className="text-xs text-gray-400 mt-1">Crea tu primera meta para empezar a medir progreso</p>
          </div>
        ) : (
          <div className="space-y-3 max-w-4xl mx-auto">
            {goals.map(goal => {
              const progress = getProgress(goal);
              const taskStats = getLinkedTaskStats(goal);
              const isExpanded = expandedGoals[goal.id];
              const statusCfg = STATUS_CONFIG[goal.estado] || STATUS_CONFIG['en-curso'];
              const linkedProjectNames = (goal.linkedProjects || []).map(pid => projects.find(p => p.id === pid)?.nombre).filter(Boolean);

              return (
                <div key={goal.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                  {/* Goal Header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <button onClick={() => setExpandedGoals(prev => ({ ...prev, [goal.id]: !prev[goal.id] }))} className="mt-0.5 text-gray-400 hover:text-gray-600">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{goal.titulo}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                          </div>
                          {goal.descripcion && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{goal.descripcion}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                            {goal.fechaLimite && <span>Limite: {new Date(goal.fechaLimite).toLocaleDateString('es-ES')}</span>}
                            {linkedProjectNames.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Link2 className="w-3 h-3" /> {linkedProjectNames.join(', ')}
                              </span>
                            )}
                            {taskStats.total > 0 && (
                              <span>{taskStats.done}/{taskStats.total} tareas</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <div className="text-right mr-2">
                          <span className="text-lg font-bold text-purple-600">{progress}%</span>
                        </div>
                        <button onClick={() => handleEdit(goal)} className="p-1 text-gray-400 hover:text-blue-500 rounded">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(goal.id)} className="p-1 text-gray-400 hover:text-red-500 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${progress === 100 ? 'bg-green-500' : progress > 60 ? 'bg-purple-500' : progress > 30 ? 'bg-blue-500' : 'bg-gray-400'}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Expanded: Key Results */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-[10px] font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> Resultados Clave
                      </p>
                      {(goal.keyResults || []).length === 0 ? (
                        <p className="text-xs text-gray-400 italic">Sin resultados clave definidos</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(goal.keyResults || []).map((kr, idx) => (
                            <div key={idx} className="flex items-center gap-2 group">
                              <button onClick={() => handleToggleKR(goal.id, idx)} className="shrink-0">
                                {kr.done ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Circle className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                                )}
                              </button>
                              <span className={`text-xs ${kr.done ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-300'}`}>{kr.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={resetForm} />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h2 className="font-bold text-gray-800 dark:text-gray-100 text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-500" />
                {editingGoal ? 'Editar Meta' : 'Nueva Meta'}
              </h2>
              <button onClick={resetForm} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Title */}
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Titulo</label>
                <input
                  type="text"
                  value={form.titulo}
                  onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-purple-500"
                  placeholder="Ej: Aumentar retención de clientes"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Descripcion</label>
                <textarea
                  value={form.descripcion}
                  onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-purple-500 resize-none"
                  placeholder="Describe el objetivo..."
                />
              </div>

              {/* Status + Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Estado</label>
                  <select
                    value={form.estado}
                    onChange={(e) => setForm(f => ({ ...f, estado: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-purple-500"
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <option key={key} value={key}>{cfg.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Fecha limite</label>
                  <input
                    type="date"
                    value={form.fechaLimite}
                    onChange={(e) => setForm(f => ({ ...f, fechaLimite: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Key Results */}
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Resultados Clave (KRs)</label>
                <div className="space-y-1.5 mb-2">
                  {form.keyResults.map((kr, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 rounded-lg">
                      <span className="text-xs text-gray-700 dark:text-gray-300 flex-1">{kr.text}</span>
                      <button onClick={() => removeKeyResult(i)} className="text-gray-400 hover:text-red-500">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newKR}
                    onChange={(e) => setNewKR(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyResult(); } }}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-purple-500"
                    placeholder="Ej: Reducir churn rate a 5%"
                  />
                  <button onClick={addKeyResult} className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200">
                    Agregar
                  </button>
                </div>
              </div>

              {/* Linked Projects */}
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Vincular Proyectos</label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {projects.map(p => (
                    <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.linkedProjects.includes(p.id)}
                        onChange={() => toggleProject(p.id)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-300">{p.nombre}</span>
                    </label>
                  ))}
                  {projects.length === 0 && (
                    <p className="text-xs text-gray-400 italic px-3">No hay proyectos disponibles</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-800">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancelar
              </button>
              <button onClick={handleSubmit} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                {editingGoal ? 'Guardar cambios' : 'Crear Meta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoalsPage;
